"use client";

import { Box, Button, Container, Typography } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { PAGE_ROUTES } from "@/app/_utils/constants";
import { trackWaitlistClick } from "@/app/_utils/waitlistAnalytics";
import { navigateToWorkWithUs } from "@/app/_utils/workWithUsAnalytics";
import HeroRotatingGallery from "./HeroRotatingGallery";
import { motion } from "framer-motion";

const ease = [0.22, 1, 0.36, 1] as const;
const fadeUp = (delay: number) => ({
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease },
});

const pageFont = '"Inter", "Helvetica Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';

function WaitlistHeroButton() {
  return (
    <Box
      sx={{
        position: "relative",
        minWidth: { xs: "100%", lg: 240 },
        borderRadius: "16px",
        p: "2px",
        background:
          "linear-gradient(120deg, rgba(255,255,255,0.55), rgba(255,255,255,0.1), rgba(255,255,255,0.4), rgba(255,255,255,0.15))",
        backgroundSize: "300% 300%",
        animation: "waitlistBorderFlow 5s ease infinite",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        boxShadow: "0 8px 32px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.04)",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow:
            "0 14px 40px rgba(0,0,0,0.45), 0 0 28px rgba(255,255,255,0.14), 0 0 0 1px rgba(255,255,255,0.08)",
        },
        "@keyframes waitlistBorderFlow": {
          "0%, 100%": { backgroundPosition: "0% 50%" },
          "50%": { backgroundPosition: "100% 50%" },
        },
      }}
    >
      <Button
        component={Link}
        to={PAGE_ROUTES.WAITLIST}
        variant="contained"
        onClick={() => trackWaitlistClick()}
        sx={{
          fontFamily: pageFont,
          fontWeight: 700,
          fontSize: "1.05rem",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "#fff",
          width: "100%",
          height: 68,
          py: 3,
          px: 5,
          borderRadius: "14px",
          border: "none",
          textDecoration: "none",
          position: "relative",
          overflow: "hidden",
          isolation: "isolate",
          background:
            "linear-gradient(145deg, rgba(32,32,36,0.92) 0%, rgba(10,10,12,0.98) 55%, rgba(18,18,22,0.96) 100%)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.14), inset 0 -1px 0 rgba(0,0,0,0.35)",
          animation: "waitlistInnerGlow 3.5s ease-in-out infinite",
          transition: "background 0.3s ease, color 0.3s ease",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            background:
              "linear-gradient(105deg, transparent 38%, rgba(255,255,255,0.16) 50%, transparent 62%)",
            transform: "translateX(-130%)",
            transition: "transform 0.7s ease",
            pointerEvents: "none",
            zIndex: 0,
          },
          "&::after": {
            content: '""',
            position: "absolute",
            inset: "auto -20% -60% -20%",
            height: "70%",
            background:
              "radial-gradient(ellipse at center, rgba(255,255,255,0.12) 0%, transparent 70%)",
            pointerEvents: "none",
            zIndex: 0,
          },
          "&:hover": {
            color: "#fff",
            background:
              "linear-gradient(145deg, rgba(42,42,48,0.96) 0%, rgba(16,16,20,1) 55%, rgba(26,26,32,0.98) 100%)",
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,0.22), inset 0 -1px 0 rgba(0,0,0,0.35)",
          },
          "&:hover::before": {
            transform: "translateX(130%)",
          },
          "& .waitlist-cta-label": {
            position: "relative",
            zIndex: 1,
            display: "inline-flex",
            alignItems: "center",
          },
          "& .waitlist-cta-arrow": {
            ml: 1,
            fontSize: 20,
            transition: "transform 0.3s ease, opacity 0.3s ease",
            opacity: 0.85,
          },
          "&:hover .waitlist-cta-arrow": {
            transform: "translateX(5px)",
            opacity: 1,
          },
          "@keyframes waitlistInnerGlow": {
            "0%, 100%": {
              boxShadow:
                "inset 0 1px 0 rgba(255,255,255,0.12), inset 0 -1px 0 rgba(0,0,0,0.35), 0 0 0 rgba(255,255,255,0)",
            },
            "50%": {
              boxShadow:
                "inset 0 1px 0 rgba(255,255,255,0.2), inset 0 -1px 0 rgba(0,0,0,0.35), 0 0 18px rgba(255,255,255,0.06)",
            },
          },
        }}
      >
        <Box component="span" className="waitlist-cta-label">
          Join Our Waitlist
          <ArrowForwardIcon className="waitlist-cta-arrow" />
        </Box>
      </Button>
    </Box>
  );
}

export default function HeroSection() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        /* Mobile: height follows content so the page scrolls; desktop keeps a tall hero */
        minHeight: { xs: "min-content", md: "88vh" },
        backgroundImage:
          "url('https://storage.googleapis.com/cuo-website/Katz%27s+Deli.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        display: "flex",
        alignItems: "flex-start",
        pt: { xs: 6, md: 10 },
        pb: { xs: 8, md: 10 },
        px: { xs: 2, md: 4 },
        boxSizing: "border-box",
      }}
    >
      {/* Dark overlay — hero only, no blur */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(0, 0, 0, 0.78)",
          zIndex: 0,
        }}
      />
      <Container maxWidth={false} sx={{ position: "relative", zIndex: 1, width: "100%", px: { xs: 2, md: 6 } }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            alignItems: { xs: "stretch", lg: "stretch" },
            gap: { xs: 4, md: 5, lg: 8 },
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              flex: { lg: "1 1 0" },
              minWidth: 0,
              minHeight: 0,
              maxWidth: { xs: 640, lg: "none" },
              pr: { lg: 2 },
              mx: { xs: "auto", lg: 0 },
              textAlign: { xs: "center", lg: "left" },
              /* Keep copy in its column so the gallery never paints over it */
              position: "relative",
              zIndex: 2,
            }}
          >
            <motion.div {...fadeUp(0.1)}>
              <Typography
                sx={{
                  fontFamily: pageFont,
                  fontSize: { xs: "0.7rem", md: "0.75rem" },
                  fontWeight: 600,
                  letterSpacing: { xs: "0.12em", md: "0.2em" },
                  color: "rgba(255,255,255,0.8)",
                  textTransform: "uppercase",
                  mb: 2.5,
                  lineHeight: 1.35,
                  wordBreak: "break-word",
                }}
              >
                New York City&apos;s Premier Comedy &amp; Magic producers
              </Typography>
            </motion.div>
            <motion.div {...fadeUp(0.22)}>
              <Typography
                component="h1"
                sx={{
                  fontFamily: pageFont,
                  fontWeight: 700,
                  fontSize: {
                    xs: "3.25rem",
                    sm: "4.25rem",
                    md: "clamp(2.75rem, 6vw, 4rem)",
                    lg: "4.75rem",
                    xl: "5.25rem",
                  },
                  lineHeight: 1.1,
                  letterSpacing: "-0.02em",
                  color: "#fff",
                  mb: 3,
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    whiteSpace: { xs: "normal", lg: "nowrap" },
                  }}
                >
                  Comedy and Magic in
                </Box>
                <Box
                  component="span"
                  sx={{
                    color: "rgba(192,192,192,0.95)",
                    fontWeight: 700,
                    display: "block",
                    whiteSpace: { xs: "normal", lg: "nowrap" },
                  }}
                >
                  Unexpected Places
                </Box>
              </Typography>
            </motion.div>
            <motion.div {...fadeUp(0.38)}>
              <Typography
                sx={{
                  fontFamily: pageFont,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  lineHeight: 1.75,
                  color: "rgba(255,255,255,0.9)",
                  mb: 5,
                }}
              >
                We produce pop-up comedy and magic shows in New York City&apos;s most iconic spaces.
              </Typography>
            </motion.div>
            <motion.div {...fadeUp(0.5)}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                flexWrap: "wrap",
                gap: 2.5,
                mb: 2,
                justifyContent: { xs: "center", lg: "flex-start" },
                alignItems: { xs: "stretch", sm: "center" },
              }}
            >
              <Button
                component={Link}
                to={PAGE_ROUTES.TICKETS}
                variant="contained"
                sx={{
                  fontFamily: pageFont,
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: "#1a1a1a",
                  backgroundColor: "#e8e8e8",
                  border: "none",
                  borderRadius: "14px",
                  height: 68,
                  py: 3,
                  px: 5,
                  minWidth: { xs: "100%", lg: 240 },
                  boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                    color: "#1a1a1a",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
                  },
                }}
              >
                Buy Tickets
              </Button>
              <Button
                variant="contained"
                onClick={() => navigateToWorkWithUs(navigate)}
                sx={{
                  fontFamily: pageFont,
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: "#fff",
                  backgroundColor: "#0d0d0d",
                  border: "2px solid rgba(255,255,255,0.35)",
                  borderRadius: "14px",
                  height: 68,
                  py: 3,
                  px: 5,
                  minWidth: { xs: "100%", lg: 240 },
                  "&:hover": {
                    backgroundColor: "#1a1a1a",
                    borderColor: "rgba(255,255,255,0.5)",
                  },
                }}
              >
                Work With Us
              </Button>
              <WaitlistHeroButton />
            </Box>
            </motion.div>

            <motion.div {...fadeUp(0.62)}>
            <Box
              sx={{
                borderTop: "1px solid rgba(255,255,255,0.15)",
                pt: 5,
                mt: 5,
                display: "flex",
                flexWrap: "wrap",
                gap: { xs: 5, lg: 10 },
                justifyContent: { xs: "center", lg: "flex-start" },
              }}
            >
              {[
                { stat: "90+", label: "Unique Venues" },
                { stat: "350+", label: "Shows Produced" },
                { stat: "Since 2021", label: "Making NYC Laugh" },
              ].map(({ stat, label }, i) => (
                <motion.div key={stat} {...fadeUp(0.62 + i * 0.1)}>
                  <Box sx={{ textAlign: { xs: "center", lg: "left" } }}>
                    <Typography sx={{ fontFamily: pageFont, fontWeight: 700, fontSize: { xs: "2.25rem", md: "2.75rem" }, color: "#fff" }}>{stat}</Typography>
                    <Typography sx={{ fontFamily: pageFont, fontSize: { xs: "1rem", md: "1.1rem" }, color: "rgba(255,255,255,0.8)" }}>{label}</Typography>
                  </Box>
                </motion.div>
              ))}
            </Box>
            </motion.div>
          </Box>

          <motion.div
            initial={{ opacity: 0, x: 32 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease }}
            style={{ display: "flex", minWidth: 0, minHeight: 0 }}
          >
          <Box
            sx={{
              flex: { lg: "0 0 auto" },
              minWidth: 0,
              minHeight: 0,
              width: { xs: "100%", lg: "clamp(300px, 32vw, 440px)" },
              maxWidth: { xs: 460, lg: 440 },
              mx: { xs: "auto", lg: 0 },
              ml: { lg: "auto" },
              display: "flex",
              flexDirection: "column",
              alignSelf: { lg: "stretch" },
              flexShrink: { lg: 1 },
            }}
          >
            <HeroRotatingGallery />
          </Box>
          </motion.div>
        </Box>
      </Container>
    </Box>
  );
}
